import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Button, InputContainer, P, Spinner } from '../../components';
import FilterTag from '~COMPONENTS/filters/FilterTag';
import TextInput from '~COMPONENTS/formInputs/TextInput';
import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { requestProductsById } from '../actions';
import { quickAddProducts } from '../../carts/actions';
import { closeModal } from '../../components/modal/actions';
import { accountDetails, getUserAccount, getUserData } from '../../user/selectors';

import { modalTypes } from '../../types/modal';
import { selectedDepartmentTypes } from '../../types/shop';
import { accountDetailTypes, userAccountTypes, userTypes } from '../../types/user';

import { removeDuplicates } from '../../util/findUnique';
import { getFundamentals, isFundamentalProduct } from '../../fundamentals/constants';
import { FUNDAMENTAL_DATA_TYPE } from '../../fundamentals/types';
import { PRODUCT_SEARCH_MAX } from '../constants';
import { getSelectedDepartment } from '../selectors';

export class QuickAddContent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      productIdList: [],
      productList: [],
      inputVal: '',
      searchError: false,
      isLoading: false
    };
  }

  setProductIdList = productIdList => this.setState({ productIdList });

  setProductList = productList => this.setState({ productList });

  setInputVal = inputVal => this.setState({ inputVal });

  setSearchError = errorMsg => this.setState({ searchError: errorMsg });

  fetchProductInfo = async productIds => {
    const { account, accountDetails, requestProductsById, selectedDepartment, user } = this.props;
    const normalizedProductIds = removeDuplicates(productIds.filter(id => id !== ''));
    if (normalizedProductIds && normalizedProductIds.length > 0) {
      const { payload } = await requestProductsById({
        accountNumber: account.number,
        deptNumber: selectedDepartment.number,
        language: user.locale.split('_')[0].toUpperCase(),
        pricelistType: accountDetails.pricelistType,
        region: accountDetails.region,
        orderType: selectedDepartment.type,
        productIds: normalizedProductIds
      });
      const { styles = [] } = payload;
      const products = styles.map(style => style.products);

      // retain same order of productIds that was passed to requestProductsById
      const flattenedProducts = [].concat(...products);
      const orderedProductList = [];
      normalizedProductIds.forEach(id => {
        const returnedProduct = flattenedProducts.find(p => p.productCode === id);
        if (returnedProduct) orderedProductList.push(returnedProduct);
      });
      this.setProductList(orderedProductList);
      this.setProductIdList(normalizedProductIds);
    }
  };

  handleProductRemove = id => {
    const { productIdList, productList } = this.state;
    this.setProductIdList(productIdList.filter(prodId => prodId !== id));
    this.setProductList(productList.filter(({ productCode }) => productCode !== id));
  };

  handleKeyPress = e => {
    if (e.key === 'Enter') {
      const { inputVal } = this.state;
      this.handleInputChange(inputVal ? inputVal.toUpperCase() : inputVal);
    }
  };

  handleInputChange = val => {
    const { inputVal, productIdList } = this.state;

    if (val && inputVal && val.toUpperCase() === inputVal.toUpperCase()) {
      const hasCommas = val.indexOf(',') > -1;
      const valList = hasCommas ? val.split(',').map(val => val.trim()) : val.trim().split(/\s+/);
      const updatedList = [...productIdList, ...valList];

      if (updatedList.length > PRODUCT_SEARCH_MAX) {
        this.setSearchError({ id: 'gridwall.search.error.tooManyProducts', values: { amount: PRODUCT_SEARCH_MAX } });
      } else {
        this.setSearchError(false);
        this.fetchProductInfo(updatedList);
        this.setInputVal('');
      }
    } else {
      this.setInputVal(val);
    }
  };

  handleSubmit = async () => {
    const { quickAddProducts, closeModal } = this.props;
    const productList = this.validProducts();
    const adjustedProductList = productList.map(prod => ({ ...prod, sizes: [] }));

    this.setState({ isLoading: true }, async () => {
      await quickAddProducts(adjustedProductList);
      closeModal();
      this.setState({ isLoading: false });
    });
  };

  productIdIsValid(id) {
    const { fundamentalData } = this.props;
    const { productList } = this.state;
    const product = productList.find(({ productCode }) => productCode === id);

    return typeof product !== 'undefined' && !isFundamentalProduct(fundamentalData, id);
  }

  /**
   * Returns a filtered array of products that are allowed to be added to the cart
   * @return {Array}
   */
  validProducts() {
    const { fundamentalData } = this.props;
    const { productList } = this.state;

    return productList.filter(({ productCode }) => !isFundamentalProduct(fundamentalData, productCode));
  }

  render() {
    const { inputVal, productIdList, searchError, isLoading } = this.state;
    const validProducts = this.validProducts();

    return (
      <div className='ta-sm-c'>
        <div className='headline-3 u-bold mb10-sm'>
          <TranslatedCopy id='gridwall.quickadd.title' />
        </div>
        <P className='mb5-sm'>
          <TranslatedCopy id='gridwall.quickadd.copy' />
        </P>
        {productIdList.map(id => (
          <FilterTag
            key={`filterTag__${id}`}
            id={`filterTag__${id}`}
            error={!this.productIdIsValid(id)}
            onClick={() => this.handleProductRemove(id)}
          >
            {id}
          </FilterTag>
        ))}
        <div className='m5-md u-full-width'>
          <InputContainer
            label={new TranslatedCopy({ id: 'gridwall.quickadd.placeholder' }).toString()}
            clear={this.clearSearch}
            error={searchError && new TranslatedCopy(searchError).toString()}
          >
            <TextInput
              name='quickAdd__input'
              value={inputVal}
              onChange={this.handleInputChange}
              onKeyPress={this.handleKeyPress}
            />
          </InputContainer>
        </div>
        <Button
          onClick={this.handleSubmit}
          extraClassName='ncss-btn-primary-dark'
          data-desc='quickAdd__button_submit'
          disabled={!validProducts.length || isLoading}
        >
          <div style={{ position: 'relative ' }}>
            {isLoading && (
              <div style={{ position: 'absolute', top: '12px', left: '68px' }}>
                <Spinner />
              </div>
            )}
            <TranslatedCopy id='gridwall.quickadd.submit' values={{ amount: validProducts.length }} />
          </div>
        </Button>
      </div>
    );
  }
}

QuickAddContent.propTypes = {
  ...modalTypes,
  account: userAccountTypes,
  accountDetails: accountDetailTypes,
  closeModal: PropTypes.func.isRequired,
  fundamentalData: FUNDAMENTAL_DATA_TYPE.isRequired,
  requestProductsById: PropTypes.func.isRequired,
  selectedDepartment: selectedDepartmentTypes,
  user: userTypes.isRequired
};

QuickAddContent.defaultProps = {
  account: null,
  accountDetails: null,
  selectedDepartment: null
};

export const mapStateToProps = state => ({
  account: getUserAccount(state),
  accountDetails: accountDetails(state),
  fundamentalData: getFundamentals(state),
  selectedDepartment: getSelectedDepartment(state),
  user: getUserData(state)
});

const connector = connect(mapStateToProps, {
  quickAddProducts,
  closeModal,
  requestProductsById
});

export default connector(QuickAddContent);
